import { PaymentMethods } from './payment-methods/PaymentMethods';
import ThreeDSecure from './three-d-secure/ThreeDSecure';
import { ThreeDSecureStatus, PaymentFlow } from './enums/Tokenization';
import { Environment } from './utils/Environment';
import { CheckoutUXFlow } from './enums/Checkout';
import {
  ClientContext,
  ClientContextFactory,
} from './core/ClientContextFactory';
import {
  CheckoutOptions,
  VaultManagerOptions,
  ClientContextOptions,
  PrimerVaultManager,
  PrimerCheckout,
  SinglePaymentMethodCheckoutOptions,
  PrimerSinglePaymentMethodCheckout,
  RenderOptions,
} from './types';
import { PaymentMethodType } from './enums/Tokens';
import { initErrorMonitoring } from './monitoring/ErrorMonitoring';
import FlowFactory from './checkout/flows/Factory';
import { BasePaymentMethod } from './payment-methods/BasePaymentMethod';
import { ApiEvent } from './analytics/constants/enums';
import { createStore } from './store/CheckoutStoreFactory';
import { createStyleManager } from './checkout/ui/StyleManager';

const version = Environment.get('PRIMER_SDK_VERSION');

// This class can't be called "PrimerClient" due to limitation of the loader + dts-bundle
export default class Client {
  public static CheckoutUXFlow = CheckoutUXFlow;

  public static ThreeDSecureStatus = ThreeDSecureStatus;

  public static PaymentFlow = PaymentFlow;

  public static PaymentMethodType = PaymentMethodType;

  public static SDK_VERSION = version;

  private options: ClientContextOptions;

  private currentContextPromise: Promise<ClientContext> | null;

  private currentContext: ClientContext | null;

  constructor(options: ClientContextOptions) {
    this.options = options;
    this.currentContextPromise = null;
    this.currentContext = null;

    initErrorMonitoring();
  }

  private async getContext(
    options:
      | CheckoutOptions
      | VaultManagerOptions
      | SinglePaymentMethodCheckoutOptions
      | RenderOptions,
  ): Promise<ClientContext> {
    if (this.currentContextPromise) return this.currentContextPromise;

    this.currentContextPromise = ClientContextFactory.create({
      options: this.options,
      renderOptions: options,
    });

    this.currentContext = await this.currentContextPromise;
    return this.currentContext;
  }

  async render(
    options: RenderOptions,
  ): Promise<Record<string, BasePaymentMethod>> {
    const context = await this.getContext(options);

    const store = createStore();
    store.setCustomerDetails(options.customerDetails ?? null, 'CLIENT');
    store.setBusinessDetails(options.businessDetails ?? null, 'CLIENT');

    if (options.purchaseInfo && !options.orderDetails) {
      console.warn('purchaseInfo is deprecated, use orderDetails instead');
      store.setPurchaseInfo(options.purchaseInfo ?? null);
    } else {
      if (options.purchaseInfo && options.orderDetails) {
        console.warn(
          'both purchaseInfo and orderDetails have been provided, orderDetails will take preference',
        );
      }
      store.setOrderDetails(options.orderDetails ?? null, 'CLIENT');
    }

    return PaymentMethods.create(
      context,
      (options as unknown) as Record<string, unknown>,
      store,
      createStyleManager(),
    );
  }

  async checkout(
    options:
      | CheckoutOptions
      | VaultManagerOptions
      | SinglePaymentMethodCheckoutOptions,
  ): Promise<
    PrimerVaultManager | PrimerCheckout | PrimerSinglePaymentMethodCheckout
  > {
    const getContext = () => this.getContext(options);
    const checkout = await FlowFactory.create(getContext, options);
    const context = await getContext();

    // End of loading
    // (start of loading is done by the context :/)
    context.analytics.call({
      event: ApiEvent.loadedCheckoutUi,
      data: {
        uxFlow: options.uxFlow,
      },
    });

    return checkout;
  }

  get threeDSecure() {
    if (this.currentContext) {
      return new ThreeDSecure({
        context: this.currentContext,
      });
    }
    return null;
  }
}
